/**
 * The whole of one turn, as the platform ran it: what the customer's message
 * settled, what that changed, the question worth asking next, anything the
 * customer has contradicted, and which outcomes may and may not be raised.
 *
 * All of it is built from what the API already returns for the turn.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import type { ChangedNode } from "@unlikelyai-magic/utils";
import { isOutcomeDecisive, normaliseString } from "@unlikelyai-magic/utils";
import {
  type OutcomeChange,
  calculateOutcomeChanges,
} from "./components/outcomeChanges";
import type { ConversationPair, TurnOutcomeState } from "./types";

export interface Contradiction {
  question: string;
  /**
   * Turn numbers, as shown to the reader, where the customer answered it.
   */
  turns: number[];
}

export interface Briefing {
  /**
   * Turn number as shown to the reader.
   */
  turnNumber: number;
  /**
   * The questions the reply before this message was chasing. The customer's
   * message is the answer to them, so they say whether the last briefing worked.
   */
  askedFor: string[];
  /**
   * The conditions this message settled, with the reasoning recorded against
   * each and the outcomes that depend on them.
   */
  learned: ChangedNode[];
  /**
   * Outcomes whose standing moved between the turn before and this one.
   */
  outcomeChanges: OutcomeChange[];
  askNext?: {
    question: string;
    /**
     * Undecided outcomes that are waiting on this question.
     */
    waitingOn: string[];
  };
  /**
   * Any further questions the platform asked for on the same turn.
   */
  alsoAsk: string[];
  contradictions: Contradiction[];
  mayRaise: TurnOutcomeState[];
  mustNotRaise: TurnOutcomeState[];
  /**
   * True while the platform is still working the turn out.
   */
  isEvaluating: boolean;
  /**
   * True when the turn produced nothing worth briefing the agent on.
   */
  isEmpty: boolean;
}

const outcomeStatesOnly = (
  states: TurnOutcomeState[] | undefined
): TurnOutcomeState[] =>
  (states ?? []).filter((state) => state.type === "suggestion");

/**
 * The turn a pair belongs to, as the reader counts them.
 *
 * A pair holds the customer's message together with the assistant text that came
 * before it, so a turn is a customer message and everything it caused. The final
 * pair can hold only the assistant's reply, which still belongs to the turn the
 * last customer message started.
 */
export const turnNumberOf = (
  conversation: ConversationPair,
  conversations: ConversationPair[]
): number => {
  const asked = conversations.filter(
    (pair) => pair.index <= conversation.index && pair.userMessage?.trim()
  ).length;
  return Math.max(asked, 1);
};

/**
 * Which turns a question was answered on, so a contradiction can say where the
 * two answers came from.
 */
const turnsAnswering = (
  conversations: ConversationPair[],
  question: string
): number[] => {
  const key = normaliseString(question);
  const turns: number[] = [];
  for (const conversation of conversations) {
    const answered = conversation.changedNodes?.some(
      (node) => normaliseString(node.question) === key
    );
    if (!answered) continue;
    const turn = turnNumberOf(conversation, conversations);
    if (!turns.includes(turn)) turns.push(turn);
  }
  return turns;
};

/**
 * The standing to compare this turn against.
 *
 * Both pairs of a turn are written with that turn's outcomes, so a pair holding
 * only the assistant's reply has to reach back one further to find the turn
 * before it.
 */
const previousOutcomeStates = (
  conversation: ConversationPair,
  conversations: ConversationPair[]
): TurnOutcomeState[] | undefined => {
  const step = conversation.userMessage?.trim() ? 1 : 2;
  return conversations.find((pair) => pair.index === conversation.index - step)
    ?.outcomeStates;
};

export const buildBriefing = (
  conversation: ConversationPair,
  conversations: ConversationPair[],
  allNodes: Map<string, ChangedNode>
): Briefing => {
  const outcomes = outcomeStatesOnly(conversation.outcomeStates);
  const mayRaise = outcomes.filter((state) => state.outcome === Outcome.TRUE);
  const mustNotRaise = outcomes.filter(
    (state) => state.outcome === Outcome.FALSE
  );

  // The briefing this pair's state produced was consumed by the reply that came
  // next, so that is where the question the agent was asked to chase is recorded.
  // A pair with no customer message is already that reply.
  const replyPair = conversation.userMessage?.trim()
    ? (conversations.find((pair) => pair.index === conversation.index + 1) ??
      conversation)
    : conversation;
  const targetQuestions = replyPair.targetQuestions ?? [];
  const targetQuestion = targetQuestions[0];
  const node = targetQuestion
    ? allNodes.get(normaliseString(targetQuestion))
    : undefined;
  const undecidedIds = new Set(
    outcomes
      .filter(
        (state) =>
          state.outcome !== Outcome.TRUE && state.outcome !== Outcome.FALSE
      )
      .map((state) => state.productTreeId)
  );
  const waitingOn = outcomes
    .filter(
      (state) =>
        undecidedIds.has(state.productTreeId) &&
        (node?.usedInIds?.has(state.productTreeId) ?? false)
    )
    .map((state) => state.name);

  const contradictions = (conversation.contradictingQuestions ?? []).map(
    (question) => ({
      question,
      turns: turnsAnswering(conversations, question),
    })
  );

  const learned = (conversation.changedNodes ?? []).filter((changedNode) =>
    isOutcomeDecisive(changedNode.value)
  );
  const outcomeChanges = calculateOutcomeChanges(
    conversation.outcomeStates,
    previousOutcomeStates(conversation, conversations)
  );

  return {
    turnNumber: turnNumberOf(conversation, conversations),
    askedFor: conversation.targetQuestions ?? [],
    learned,
    outcomeChanges,
    askNext: targetQuestion
      ? { question: targetQuestion, waitingOn }
      : undefined,
    alsoAsk: targetQuestions.slice(1),
    contradictions,
    mayRaise,
    mustNotRaise,
    isEvaluating: conversation.isLoadingSystemThinking ?? false,
    isEmpty:
      !targetQuestion &&
      contradictions.length === 0 &&
      mayRaise.length === 0 &&
      mustNotRaise.length === 0 &&
      learned.length === 0 &&
      outcomeChanges.length === 0,
  };
};

/**
 * Reads a contradiction as one sentence. The API reports which question was
 * answered two ways, not the two answers themselves, so the sentence names the
 * turns rather than quoting them.
 */
export const contradictionSentence = (contradiction: Contradiction): string => {
  const { question, turns } = contradiction;
  if (turns.length < 2) {
    return `The customer has given conflicting answers to “${question}”, so it has gone back to unknown.`;
  }
  const list = turns.slice(0, -1).join(", ");
  const last = turns[turns.length - 1];
  return `The customer answered “${question}” on turn ${list} and again on turn ${last}, so it has gone back to unknown.`;
};
